import { Box, Divider, Grid, SelectChangeEvent } from "@mui/material";
import { User } from "../../../../../model/OrganizationModel";
import { Check, Mode } from "@mui/icons-material";
import { KeyDriveh2Header } from "../../../../../components/KeydriveComponents/Fonts/Headers/KeyDriveh2Header";
import { KeyDriveBodyMax } from "../../../../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMax";
import { KeydriveTextfield } from "../../../../../components/KeydriveComponents/Textfield/KeydriveTextfield";
import { KeyDriveSelect } from "../../../../../components/KeydriveComponents/Selects/KeyDriveSelect";
import { KeyDriveBodyMin } from "../../../../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMin";
import { KeyDrivePrimaryButton } from "../../../../../components/KeydriveComponents/Buttons/KeyDrivePrimaryButton";
import EditFolderNotifications from "./EditFolderNotifications";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";


interface ISensibility {
    text: string;
    sensitivity: EnumSensitivity;
}

enum EnumSensitivity {
    low = 0,
    medium,
    high
}

interface IEditFolderInformation {
    folderName: string;
    handleSensitivity(e: SelectChangeEvent): void;
    handleUser(e: SelectChangeEvent): void;
    newUserSearch: string;
    newSensitivity: number;
    admOrganizationUsers: User[];
    updateInformation: () => void;
    setSelectNotification: (set: boolean) => void;
    selectNotification: boolean;
    setNameChanged: (set: string) => void;
    nameChanged: string;
    hasFather: boolean;
    ownerId: string;
    setNewUserSearch: (set: string) => void;
}

function EditFolderInformation({
    folderName,
    handleSensitivity,
    handleUser,
    newSensitivity,
    newUserSearch,
    admOrganizationUsers,
    updateInformation,
    selectNotification,
    setSelectNotification,
    setNameChanged,
    nameChanged,
    hasFather,
    ownerId,
    setNewUserSearch
}: IEditFolderInformation) {
    const { t } = useTranslation();

    useEffect(() => {
        const found = admOrganizationUsers.some(user => user.id === ownerId);
        if (found) {
            setNewUserSearch(ownerId);
        }
    }, [ownerId, admOrganizationUsers]);


    return (
        <>
            <Grid
                item
                display="flex"
                alignItems="center"
                xs={12}
            >
                <Mode 
                    fontSize='large'
                    sx={{
                        marginRight: "5px"
                    }}
                />
                <KeyDriveh2Header 
                    needBold
                    needEllipses={false}
                    color='#2D2D2D'
                    text={t('EditFolder.title')}
                />
                <Divider />
            </Grid>
            <Grid item xs={12} mt="15px">
                <KeyDriveBodyMax
                    color='#2D2D2D'
                    needBold
                    needEllipses={false}
                    text={t('EditFolder.information')}
                />
            </Grid>
            <Grid item xs={12} mb="15px">
                <KeydriveTextfield 
                    label={t('EditFolder.folderName')}
                    onChange={(e) => {
                        setNameChanged(e.currentTarget.value);
                    }}
                    value={nameChanged}
                    needFullWidth
                />
            </Grid>
            <Grid item xs={12}>
                <KeyDriveSelect<ISensibility>
                    renderItem={(item) => (
                        <Box display="flex" alignItems="center">
                            {item.text} 
                            <Box ml="4px">
                                <KeyDriveBodyMin
                                    text={`(${item.text})`}
                                    color="#2D2D2D"
                                    needBold={false}
                                    needEllipses={false}
                                    opacityParams={{
                                        needOpacity: true,
                                        opacityLevel: "0.5"
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                    keyExtract={(item) => item.sensitivity.toString()}
                    items={[
                        {
                            sensitivity: 0,
                            text: t('Sensibility.low')
                        },
                        {
                            sensitivity: 1,
                            text: t('Sensibility.medium')
                        },
                        {
                            sensitivity: 2,
                            text: t('Sensibility.higth')
                        },
                    ]}
                    onChange={handleSensitivity}
                    value={newSensitivity}
                    label={t('EditFolder.sensitivity')}
                    needFullWidth
                />
            </Grid>
            <Grid item xs={12} mt="15px">
                <KeyDriveSelect<User>
                    renderItem={(item) => (
                        <Box display="flex" alignItems="center">
                            {item.name} 
                            <Box ml="4px">
                                <KeyDriveBodyMin
                                    text={`(${item.login})`}
                                    color="#2D2D2D"
                                    needBold={false}
                                    needEllipses={false}
                                    opacityParams={{
                                        needOpacity: true,
                                        opacityLevel: "0.5"
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                    keyExtract={(item) => item.id}
                    items={admOrganizationUsers}
                    onChange={handleUser}
                    value={
                        admOrganizationUsers.some(user => user.id === newUserSearch)
                            ? newUserSearch
                            : ""
                    }
                    label={t('EditFolder.owner')}
                    needFullWidth
                    disabled={hasFather}
                />
            </Grid>
        <EditFolderNotifications 
            setSelectNotification={setSelectNotification}
            selectNotification={selectNotification}
        />
        <Grid item xs={12} mt="20px">
            <KeyDrivePrimaryButton 
                onClick={() => { updateInformation() }}
                TextComponent={KeyDriveBodyMin}
                TextComponentProps={{
                    color: "",
                    needBold: false,
                    needEllipses: false,
                    text: t('EditFolder.updateInformation')
                }}
                disabled={false}
                startIcon={<Check />}
                height="36px"
                needFullWidth
            />
        </Grid>
        </>
    );
}

export default EditFolderInformation;